import React, { useEffect, useState } from 'react';
import { planName, nextPlan } from '../utils/plans';
import { useNavigate } from 'react-router-dom';

/**
 * LimitReachedModal — Global modal that listens for tier limit errors
 * dispatched by the api.js interceptor and shows an upgrade prompt.
 *
 * Triggered by: window event 'alphagrade:tier-limit'
 * Payload shape: {
 *   error: 'limit_reached' | 'upgrade_required',
 *   message: string,
 *   current_tier?: string,
 *   limit?: number,
 *   used?: number,
 *   feature?: string,
 *   upgrade_url?: string
 * }
 */
// Plan names and prices come from utils/plans.js. These were hard-coded to
// the retired Starter $15 / Pro $35 ladder, so the cap-hit moment offered a
// plan that doesn't exist, and Core/Analyst users saw "Current plan: core".

export default function LimitReachedModal() {
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => setDetail(e.detail);
    window.addEventListener('alphagrade:tier-limit', handler);
    return () => window.removeEventListener('alphagrade:tier-limit', handler);
  }, []);

  if (!detail) return null;

  const close = () => setDetail(null);
  const goUpgrade = () => {
    close();
    navigate(detail.upgrade_url || '/pricing');
  };

  const isFeatureLock = detail.error === 'upgrade_required';
  const tier = detail.current_tier || 'free';
  const tierLabel = planName(tier);
  const next = nextPlan(tier);

  // Pick icon and title based on error type
  const icon = isFeatureLock ? '🔒' : '⏱️';
  const title = isFeatureLock ? 'Upgrade Required' : "You've Hit Your Daily Limit";

  return (
    <div
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card, #1A1F2E)',
          border: '1px solid rgba(100,149,237,0.3)',
          borderRadius: 16,
          padding: '2rem',
          maxWidth: 480,
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          position: 'relative',
        }}
      >
        {/* Close button */}
        <button
          onClick={close}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted, #64748B)',
            fontSize: 24,
            cursor: 'pointer',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted, #64748B)')}
        >
          ×
        </button>

        {/* Icon + Title */}
        <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: 48, marginBottom: '0.5rem' }}>{icon}</div>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'var(--text-primary, #F1F5F9)',
          }}>
            {title}
          </h2>
        </div>

        {/* Message */}
        <p style={{
          color: 'var(--text-secondary, #94A3B8)',
          fontSize: '0.95rem',
          lineHeight: 1.6,
          textAlign: 'center',
          margin: '0 0 1rem',
        }}>
          {detail.message}
        </p>

        {/* Usage display (only for limit_reached) */}
        {!isFeatureLock && detail.limit !== undefined && (
          <div style={{
            background: 'rgba(0,0,0,0.25)',
            border: '1px solid rgba(100,149,237,0.15)',
            borderRadius: 8,
            padding: '0.85rem 1rem',
            marginBottom: '1.25rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.85rem',
          }}>
            <span style={{ color: 'var(--text-muted, #64748B)' }}>
              Current plan: <strong style={{ color: 'var(--text-primary, #F1F5F9)' }}>{tierLabel}</strong>
            </span>
            <span style={{ color: '#EF4444', fontWeight: 700 }}>
              {detail.used} / {detail.limit}
            </span>
          </div>
        )}

        {/* CTA buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
          <button
            onClick={goUpgrade}
            style={{
              padding: '0.85rem 1.5rem',
              background: 'linear-gradient(135deg, #6495ED, #00D4AA)',
              border: 'none',
              borderRadius: 10,
              color: '#0B0F19',
              fontSize: '0.95rem',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              letterSpacing: '0.02em',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(100,149,237,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {next ? `Upgrade to ${next.name} — ${next.price}` : 'View Pricing'}
          </button>

          <button
            onClick={close}
            style={{
              padding: '0.7rem 1.5rem',
              background: 'transparent',
              border: '1px solid var(--border-medium, #334155)',
              borderRadius: 10,
              color: 'var(--text-secondary, #94A3B8)',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-strong, #475569)';
              e.currentTarget.style.color = 'var(--text-primary, #F1F5F9)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-medium, #334155)';
              e.currentTarget.style.color = 'var(--text-secondary, #94A3B8)';
            }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
